import { CheckCircle, AlertCircle, HelpCircle } from "react-feather";
import { KubernetesNode } from "../../../schema/types/mk8s/node";
import { HEALTH_COLORS } from "../../health/utils";

export const NODE_POOL_LABEL = "mk8s.cpln.io/nodepool";
const UNASSIGNED_POOL = "__unassigned__";

export type NodeStatus = "ready" | "notReady" | "unknown";

export interface NodeConditionInfo {
  type: string;
  healthy: boolean;
}

export interface ProcessedNode {
  node: KubernetesNode;
  name: string;
  uid: string;
  status: NodeStatus;
  statusLabel: string;
  conditions: NodeConditionInfo[];
  nodePool: string;
  instanceType: string;
  zone: string;
  internalIP: string;
  externalIP: string;
  kubeletVersion: string;
  osImage: string;
  kernelVersion: string;
  architecture: string;
  creationTimestamp: string;
  allocatableCpu: string;
  allocatableMemory: string;
}

export const NODE_STATUS_CONFIG: Record<
  NodeStatus,
  {
    color: string;
    bg: string;
    icon: typeof CheckCircle;
    label: string;
  }
> = {
  ready: { color: HEALTH_COLORS.ready, bg: "rgba(16, 185, 129, 0.1)", icon: CheckCircle, label: "Ready" },
  notReady: { color: HEALTH_COLORS.error, bg: "rgba(239, 68, 68, 0.1)", icon: AlertCircle, label: "Not Ready" },
  unknown: { color: HEALTH_COLORS.inactive, bg: "rgba(107, 114, 128, 0.1)", icon: HelpCircle, label: "Unknown" },
};

// Standard K8s semantics:
// - Ready: True = healthy, False = not ready
// - MemoryPressure/DiskPressure/PIDPressure/NetworkUnavailable: False = healthy (no pressure), True = problem
const NEGATIVE_CONDITIONS = new Set(["MemoryPressure", "DiskPressure", "PIDPressure", "NetworkUnavailable"]);

export function getNodeStatus(node: KubernetesNode): NodeStatus {
  const readyCondition = node.status?.conditions?.find((c) => c.type === "Ready");
  if (!readyCondition) return "unknown";
  if (readyCondition.status === "True") return "ready";
  if (readyCondition.status === "False") return "notReady";
  return "unknown";
}

export function buildStatusLabel(node: KubernetesNode): string {
  const conditions = node.status?.conditions || [];
  const problematic: string[] = [];

  for (const c of conditions) {
    if (c.type === "Ready") {
      if (c.status === "False") {
        problematic.push("NotReady");
      }
    } else if (NEGATIVE_CONDITIONS.has(c.type)) {
      if (c.status === "True") {
        problematic.push(c.type);
      }
    }
  }

  let label = problematic.length > 0 ? problematic.join(",") : "Ready";

  if (node.spec?.unschedulable) {
    label += ",SchedulingDisabled";
  }

  return label;
}

export function getNodePool(node: KubernetesNode): string {
  return node.metadata.labels?.[NODE_POOL_LABEL] || UNASSIGNED_POOL;
}

function getConditions(node: KubernetesNode): NodeConditionInfo[] {
  return (node.status?.conditions || []).map((c) => ({
    type: c.type,
    healthy:
      c.type === "Ready"
        ? c.status === "True"
        : NEGATIVE_CONDITIONS.has(c.type)
          ? c.status === "False"
          : c.status === "True",
  }));
}

export function formatCpu(cpu: string): string {
  if (!cpu) return "-";
  if (cpu.endsWith("m")) {
    const millicores = parseInt(cpu);
    if (isNaN(millicores)) return cpu;
    const cores = millicores / 1000;
    return cores.toFixed(1).replace(/\.0$/, "");
  }
  return cpu;
}

export function formatMemory(memory: string): string {
  if (!memory) return "-";
  const match = memory.match(/^(\d+)\s*(Ki|Mi|Gi|Ti)?$/);
  if (!match) return memory;
  const value = parseInt(match[1]);
  const unit = match[2] || "";
  let gi: number;
  switch (unit) {
    case "Ki":
      gi = value / 1048576;
      break;
    case "Mi":
      gi = value / 1024;
      break;
    case "Gi":
      gi = value;
      break;
    case "Ti":
      gi = value * 1024;
      break;
    default:
      gi = value / 1073741824;
      break;
  }
  if (gi >= 1) return `${gi.toFixed(1).replace(/\.0$/, "")} Gi`;
  return `${(gi * 1024).toFixed(0)} Mi`;
}

function getAddress(node: KubernetesNode, type: string): string {
  return node.status?.addresses?.find((a) => a.type === type)?.address || "";
}

export function processNodes(nodes: KubernetesNode[]): ProcessedNode[] {
  return nodes.map<ProcessedNode>((node) => {
    const status = getNodeStatus(node);

    return {
      node,
      name: node.metadata.name,
      uid: node.metadata.uid || node.metadata.name,
      status,
      statusLabel: buildStatusLabel(node),
      conditions: getConditions(node),
      nodePool: getNodePool(node),
      instanceType: node.metadata.labels?.["node.kubernetes.io/instance-type"] || "",
      zone: node.metadata.labels?.["topology.kubernetes.io/zone"] || "",
      internalIP: getAddress(node, "InternalIP"),
      externalIP: getAddress(node, "ExternalIP"),
      kubeletVersion: node.status?.nodeInfo?.kubeletVersion || "",
      osImage: node.status?.nodeInfo?.osImage || "",
      kernelVersion: node.status?.nodeInfo?.kernelVersion || "",
      architecture: node.status?.nodeInfo?.architecture || "",
      creationTimestamp: node.metadata.creationTimestamp || "",
      allocatableCpu: formatCpu(node.status?.allocatable?.cpu || ""),
      allocatableMemory: formatMemory(node.status?.allocatable?.memory || ""),
    };
  }).sort((a, b) => a.nodePool.localeCompare(b.nodePool));
}
